/**
 * Resolves a URL step slug to a stepper index.
 *
 * Given a slug from the `:step` route parameter, a bidirectional slug map, and the
 * current list of steps, this function finds the index of the matching step so the
 * builder component can activate it on init.
 *
 * Only applicable for `edit-[item]/:id/:step` routes. The caller is responsible for
 * ensuring this is only invoked when the route and ID param conditions are met.
 *
 * @param stepSlug - The raw `:step` value from the route params (e.g., `'security'`).
 * @param slugMap - A map of slug strings to step label values (e.g., `WORKFLOW_STEP_SLUG_MAP`).
 * @param steps - The ordered array of stepper steps, each with a `label` property.
 * @returns The zero-based index of the matched step, or `-1` if no match is found.
 *
 * @example
 * const index = resolveStepSlugIndex('security', WORKFLOW_STEP_SLUG_MAP, this.steps);
 * if (index !== -1) this.currentStep = index;
 */
export function resolveStepSlugIndex<TStep extends string>(
  stepSlug: string | undefined,
  slugMap: Record<string, TStep>,
  steps: { label: string }[]
): number {
  if (!stepSlug) return -1;

  const matchedStep = slugMap[stepSlug];
  if (!matchedStep) return -1;

  return steps.findIndex(s => s.label === matchedStep);
}

